'use client';

/**
 * ObeyaCostPlanReviewSelectionsCard — the Concertina's second body
 * section: `mcp_rev_diff`'s `selection_changes` — which requirement's
 * WINNING vendor/product pick moved since tagging. Lever 2 of the
 * three real "what can change a plan" levers (total qty, product/
 * vendor selection, room allocation) — a genuinely different concept
 * from `review_groups`' per-line field changes (mental move 1), not a
 * variant row inside that list.
 *
 * Real gap this closes: the retired flat `ObeyaCostPlanRevDiffCard`
 * rendered this tier; when the Concertina replaced it, only
 * `review_groups` carried over. `selection_changes` kept computing
 * server-side (queries/rev.py, rev_selection_diff) the whole time —
 * nothing was rendering it. Restored here as its own section (below
 * `obeya_costplan_review_groups`) rather than folded into it, so its
 * different shape (no $ impact — see below) doesn't have to fake
 * review_groups' impact-bar treatment.
 *
 * No $ impact bar: NOT a data gap — rev_selection_diff_raw already
 * joins each winner's own ValueState for product_name/vendor_name;
 * unit_cost/quantity are right there, just not selected into the
 * response (a real correction after an earlier, unchecked claim that
 * this needed a bigger query change). Left out anyway, deliberately:
 * a winner swap's dollar effect already shows up in Watch's plan
 * total (a different line just counts toward the sum) — a per-row $
 * here would restate the total, not surface something new.
 *
 * Shares ONE `mcp_rev_diff` round-trip with the sibling header/groups
 * cards via React Query dedup on (strategy, params).
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — obeya_costplan_review_selections
 * @see klyde_edge/klyde_intelligence/projects/queries/rev.py — rev_selection_diff
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-review-groups-card.tsx — sibling body card (line changes)
 *
 * @component_role: Card
 * @component_type: Overlay Body Card
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useMcpRevDiff, type McpRevSelectionChange } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCostPlanCompareStore } from '@/lib/stores/costplan-compare-store';

function SelectionChangeRow({ sc, L }: { sc: McpRevSelectionChange; L: Record<string, string> }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border">
      <span className={cn(text.muted, 'line-through truncate max-w-[40%]')}>
        {sc.pinned_winner.product_name ?? sc.pinned_winner.line_id}
      </span>
      <ArrowLeftRight className="w-3 h-3 text-muted-foreground shrink-0" />
      <span className={cn(text.title, 'truncate flex-1')}>
        {sc.current_winner ? (sc.current_winner.product_name ?? sc.current_winner.line_id) : (L.unselected_since ?? '')}
      </span>
    </div>
  );
}

export function ObeyaCostPlanReviewSelectionsCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const lang = useLanguageStore((s) => s.lang);

  const compareRevId = useCostPlanCompareStore((s) => s.compareRevId);
  const compareMcpId = useCostPlanCompareStore((s) => s.compareMcpId);
  const compareAgainstRevId = useCostPlanCompareStore((s) => s.compareAgainstRevId);
  const effectiveRevId = compareMcpId && mcpId && compareMcpId === mcpId ? compareRevId : null;
  const effectiveAgainstRevId = effectiveRevId ? compareAgainstRevId : null;

  const diffStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rev_diff'));
  const { data: raw, isLoading } = useMcpRevDiff(diffStrategy, mcpId ?? null, effectiveRevId, effectiveAgainstRevId, lang ?? undefined);
  const diff = raw?.data;
  const L = diff?.labels ?? {};
  const selectionChanges = diff?.selection_changes ?? [];

  const queryKey = ['strategy-data', diffStrategy, { semantic_id: mcpId ?? '', rev_sid: effectiveRevId ?? '', ...(effectiveAgainstRevId ? { compare_rev_sid: effectiveAgainstRevId } : {}), ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_review_selections',
    surface: 'overlay' as const,
    status: (effectiveRevId ? (diff ? 'loaded' : 'loading') : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: effectiveRevId && diffStrategy ? queryKey : null,
    text: selectionChanges.length > 0 ? `${selectionChanges.length} ${L.selection_changes_header ?? ''}` : (L.no_selection_changes ?? ''),
    json: diff ? { selection_changes: diff.selection_changes } : null,
  }), [contentKey, effectiveRevId, diff, diffStrategy, queryKey, selectionChanges.length, L.selection_changes_header, L.no_selection_changes]));

  if (!effectiveRevId) return null;

  return (
    <div className="pt-2">
      <div className={cn(text.eyebrow, 'px-3 pb-1.5')}>{L.selection_changes_header ?? ''}</div>
      {isLoading ? (
        <div className={cn(text.muted, 'px-3 py-2')}>&hellip;</div>
      ) : selectionChanges.length === 0 ? (
        <div className={cn(text.muted, 'px-3 py-2')}>{L.no_selection_changes ?? ''}</div>
      ) : (
        <div className="flex flex-col gap-1.5 px-3">
          {selectionChanges.map((sc) => (
            <SelectionChangeRow key={sc.source_line_sid} sc={sc} L={L} />
          ))}
        </div>
      )}
    </div>
  );
}
